import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMySubscription } from "@/hooks/useSubscription";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Check, Loader2, Crown, Sparkles, Zap, CalendarClock, Heart, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function usePlans() {
  return useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/plans");
      return data.plans || data || [];
    },
    staleTime: 10 * 60_000,
  });
}

// Iter-285b — friendly ISO → "5 Aug 2026" formatter for the Cancel UI.
function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch { return ""; }
}

export default function PlansPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const plansQ = usePlans();
  const subQ = useMySubscription();

  const sub = subQ.data?.subscription || null;
  const usage = subQ.data?.usage || {};
  const currentSlug = sub?.plan_slug || "free";
  // Iter-285b fix (iteration_114): only treat a plan as "current" for a
  // signed-in user — guests always see the CTA on every plan, including
  // the Free card's Get started button.
  const signedIn = Boolean(user);
  const isRecurring = Boolean(usage.recurring);
  const willCancel = Boolean(usage.will_cancel);
  const activeUntil = sub?.ends_at || sub?.active_until || usage.ends_at || null;
  const isCourtesy = signedIn && currentSlug === "comp-unlimited";

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const checkoutMut = useMutation({
    mutationFn: async (plan_slug) => {
      const { data } = await sharedApi.post("/classifieds/subscriptions/checkout", {
        plan_slug,
        origin_url: window.location.origin,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't start checkout — please try again.");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Checkout failed"),
  });

  // Iter-285b — POST /classifieds/subscriptions/cancel.
  // Plan stays active until `active_until`, then auto-drops to Free.
  const cancelMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/subscriptions/cancel", {});
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Your subscription will end at the current period.");
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["subscription-me"] });
      qc.invalidateQueries({ queryKey: ["plan-usage"] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Couldn't cancel — please try again."),
  });

  // Iter-286 — POST /classifieds/subscriptions/resume un-cancels a
  // will-cancel plan before the period ends.
  const resumeMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/subscriptions/resume", {});
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Your subscription is back on — thanks for staying!");
      qc.invalidateQueries({ queryKey: ["subscription-me"] });
      qc.invalidateQueries({ queryKey: ["plan-usage"] });
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Couldn't resume — please try again."),
  });

  const handleChoose = (plan) => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/plan-and-pricing")}`);
      return;
    }
    const slug = plan.slug || plan.plan_slug;
    if (!slug) return;
    if (Number(plan.price) === 0 || slug === "free") {
      toast.info("You're on the Free plan by default.");
      return;
    }
    checkoutMut.mutate(slug);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Plans & Pricing" description="Choose a plan that fits how you sell on Allsale Classified — Free, Basic and Premium." />
      <Breadcrumbs items={[{ label: "Plans & Pricing" }]} />

      <div className="mt-6 text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3 py-1 text-xs font-bold uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> Simple, honest pricing
        </div>
        <h1 className="mt-3 font-display text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
          Plans & Pricing
        </h1>
        <p className="mt-3 text-slate-600">
          Paid plans renew monthly — cancel any time, no lock-in.
        </p>
      </div>

      {plansQ.isLoading && (
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-96 rounded-2xl" />)}
        </div>
      )}

      {!plansQ.isLoading && (
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {(plansQ.data || []).map((p) => {
            const slug = p.slug || p.plan_slug;
            const isCurrent = signedIn && slug === currentSlug;
            const popular = p.is_popular || slug === "basic";
            const isPaid = Number(p.price) > 0;
            return (
              <div
                key={slug}
                data-testid={`plan-card-${slug}`}
                className={`relative rounded-2xl p-8 border transition-colors ${
                  popular
                    ? "bg-gradient-to-br from-slate-900 via-slate-900 to-[#0F70A8] text-white border-transparent shadow-dropdown scale-[1.02]"
                    : "bg-white border-soft shadow-card"
                }`}
              >
                {popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[color:var(--brand-orange)] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 shadow-card">
                    Most Popular
                  </div>
                )}
                <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${popular ? "text-white/80" : "text-slate-500"}`}>
                  <Crown className="w-3.5 h-3.5" /> {p.name || slug}
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-black">
                    {Number(p.price) === 0 ? "Free" : `$${p.price}`}
                  </span>
                  {isPaid && (
                    <span className={`text-sm ${popular ? "text-white/70" : "text-slate-400"}`}>/ {p.period || "month"}</span>
                  )}
                </div>
                {isPaid && (
                  <p className={`mt-1 text-xs ${popular ? "text-white/70" : "text-slate-400"}`} data-testid={`plan-renew-note-${slug}`}>
                    per month — renews until you cancel
                  </p>
                )}
                {p.tagline && <p className={`mt-2 ${popular ? "text-white/85" : "text-slate-500"}`}>{p.tagline}</p>}

                <ul className={`mt-6 space-y-3 text-sm ${popular ? "text-white/90" : "text-slate-700"}`}>
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${popular ? "text-[color:var(--brand-orange)]" : "text-emerald-500"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                  {p.listing_limit != null && (
                    <li className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${popular ? "text-[color:var(--brand-orange)]" : "text-emerald-500"}`} />
                      <span>{p.listing_limit === -1 ? "Unlimited active ads" : `${p.listing_limit} active ads`}</span>
                    </li>
                  )}
                  {p.boost_credits != null && p.boost_credits > 0 && (
                    <li className="flex items-start gap-2">
                      <Zap className="w-4 h-4 mt-0.5 shrink-0 text-[color:var(--brand-orange)]" />
                      <span>{p.boost_credits} boost credits per period</span>
                    </li>
                  )}
                </ul>

                {/* Iter-285b — current-plan management block */}
                {isCurrent && (
                  <CurrentPlanBlock
                    slug={slug}
                    popular={popular}
                    isCourtesy={isCourtesy}
                    isRecurring={isRecurring}
                    willCancel={willCancel}
                    activeUntil={activeUntil}
                    onCancelClick={() => setConfirmOpen(true)}
                    cancelling={cancelMut.isPending}
                    onResumeClick={() => resumeMut.mutate()}
                    resuming={resumeMut.isPending}
                  />
                )}

                {!isCurrent && (
                  <button
                    data-testid={`plan-cta-${slug}`}
                    onClick={() => handleChoose(p)}
                    disabled={checkoutMut.isPending}
                    className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-3 transition-colors disabled:opacity-60 ${
                      popular
                        ? "bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                        : "bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white"
                    }`}
                  >
                    {checkoutMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    {Number(p.price) === 0 ? "Get started" : "Choose plan"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-16 text-center text-sm text-slate-500">
        Need something custom for your business?{" "}
        <Link to="/contact-us" className="font-semibold text-[color:var(--brand-blue)] hover:underline">
          Talk to us
        </Link>.
      </div>

      {confirmOpen && (
        <CancelConfirmDialog
          planName={sub?.plan_name || currentSlug}
          endsAt={activeUntil}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => cancelMut.mutate()}
          submitting={cancelMut.isPending}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Current-plan management block                                */
/* ─────────────────────────────────────────────────────────── */

function CurrentPlanBlock({ slug, popular, isCourtesy, isRecurring, willCancel, activeUntil, onCancelClick, cancelling, onResumeClick, resuming }) {
  const badgeBase = popular ? "bg-white/10 text-white border-white/25" : "bg-slate-100 text-slate-700 border-slate-200";
  const mutedText = popular ? "text-white/70" : "text-slate-500";

  // Iter-285b §4 — admin-granted plan (comp-unlimited) shows "Courtesy plan",
  // no cancel button. Distinguishes it from a paid recurring plan.
  if (isCourtesy) {
    return (
      <div className="mt-8 space-y-2">
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${badgeBase}`} data-testid={`plan-courtesy-${slug}`}>
          <Heart className="w-3.5 h-3.5" /> Courtesy plan
        </div>
        <p className={`text-xs ${mutedText}`}>Enjoy — this one&apos;s on us. No renewal required.</p>
      </div>
    );
  }

  // Iter-285b §3 — after-cancel state. Iter-286 — offer Resume so the
  // subscriber can un-cancel before the period ends.
  if (willCancel) {
    return (
      <div className="mt-8 space-y-2">
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${badgeBase}`} data-testid={`plan-cancel-badge-${slug}`}>
          <CalendarClock className="w-3.5 h-3.5" />
          {activeUntil ? `Cancels on ${formatDate(activeUntil)}` : "Cancels at period end"}
        </div>
        <p className={`text-xs ${mutedText}`}>
          You&apos;ll keep this plan until then. After that, you&apos;ll move to the Free plan.
        </p>
        <button
          type="button"
          onClick={onResumeClick}
          disabled={resuming}
          data-testid={`plan-resume-btn-${slug}`}
          className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-2.5 text-sm transition-colors disabled:opacity-60 ${
            popular
              ? "bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
              : "bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white"
          }`}
        >
          {resuming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          Resume subscription
        </button>
      </div>
    );
  }

  // Iter-285b §2 — recurring & not cancelled: show Cancel button.
  if (isRecurring) {
    return (
      <div className="mt-8 space-y-2">
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${badgeBase}`}>
          <Check className="w-3.5 h-3.5" /> Your current plan
        </div>
        <button
          type="button"
          onClick={onCancelClick}
          disabled={cancelling}
          data-testid={`plan-cancel-btn-${slug}`}
          className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full font-bold py-2.5 text-sm transition-colors disabled:opacity-60 ${
            popular
              ? "bg-white/10 hover:bg-white/20 text-white border border-white/25"
              : "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200"
          }`}
        >
          {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
          Cancel subscription
        </button>
      </div>
    );
  }

  // Free plan (no recurring flag) — just a static marker.
  return (
    <div className="mt-8">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${badgeBase}`}>
        <Check className="w-3.5 h-3.5" /> Your current plan
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Cancel confirmation dialog                                   */
/* ─────────────────────────────────────────────────────────── */

function CancelConfirmDialog({ planName, endsAt, onClose, onConfirm, submitting }) {
  const endsText = endsAt ? formatDate(endsAt) : "the end of the current period";
  const confirmRef = React.useRef(null);
  const titleId = React.useId();
  const bodyId = React.useId();

  // Iter-285b fix (iteration_114): a11y — Escape to dismiss, initial focus
  // on the destructive-confirm button, focus restored on close by the
  // opener (dialog is unmounted when confirmOpen flips false).
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    // Delay focus one tick so react-dom has painted the button.
    const t = setTimeout(() => confirmRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose, submitting]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="plan-cancel-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 md:p-7"
        data-testid="plan-cancel-dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--brand-orange)]">
              Cancel subscription
            </div>
            <h3 id={titleId} className="mt-1 font-display text-xl font-black text-slate-900">
              Keep {planName} until {endsText}?
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="plan-cancel-close"
            className="w-8 h-8 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p id={bodyId} className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="plan-cancel-body">
          You&apos;ll keep {planName} until <span className="font-semibold text-slate-900">{endsText}</span>, then automatically move to the Free plan. Your ads stay live, but paid features (extra listings, boost credits) will pause after that date.
        </p>

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="plan-cancel-keep"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 transition-colors disabled:opacity-60"
          >
            Keep my plan
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            data-testid="plan-cancel-confirm"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 transition-colors disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Cancel subscription
          </button>
        </div>
      </div>
    </div>
  );
}
